import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@/components/ui';
import { ProfileCard } from '@/features/onboarding/components/ProfileCard';
import { useOnboarding } from '@/features/onboarding/hooks/useOnboarding';
import { useWelcomeTour, WelcomeTour } from '@/features/welcome-tour';
import type { ProfileType } from '@/types';

const PROFILES: ProfileType[] = [
  'engineer',
  'product_manager',
  'designer',
  'founder',
  'generalist',
];

const ROLES = [
  { value: 'software_engineer', label: 'Software Engineer' },
  { value: 'tech_lead', label: 'Tech Lead' },
  { value: 'engineering_manager', label: 'Engineering Manager' },
  { value: 'staff_engineer', label: 'Staff / Principal Engineer' },
  { value: 'cto', label: 'CTO / VP Engineering' },
  { value: 'product_manager', label: 'Product Manager' },
  { value: 'product_designer', label: 'Product Designer' },
  { value: 'ux_designer', label: 'UX Designer' },
  { value: 'founder', label: 'Founder / CEO' },
  { value: 'other', label: 'Other' },
];

const INDUSTRIES = [
  { value: 'tech_saas', label: 'Tecnologia / SaaS' },
  { value: 'fintech', label: 'Fintech' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'healthtech', label: 'Healthtech' },
  { value: 'edtech', label: 'Edtech' },
  { value: 'consulting', label: 'Consultoria' },
  { value: 'agency', label: 'Agência' },
  { value: 'startup', label: 'Startup (Early-stage)' },
  { value: 'enterprise', label: 'Empresa estabelecida' },
  { value: 'other', label: 'Outra' },
];

export function OnboardingPage() {
  const { t } = useTranslation();
  const { hasSeenTour, completeTour } = useWelcomeTour();
  const {
    step,
    totalSteps,
    name,
    setName,
    selectedProfiles,
    role,
    industry,
    toggleProfile,
    setRole,
    setIndustry,
    nextStep,
    prevStep,
    skipStep3,
    isLoading,
    canProceed,
  } = useOnboarding();

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isLoading) return;

      const target = e.target as HTMLElement;
      const isInputFocused =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';

      // Enter key works everywhere to proceed
      if (e.key === 'Enter' && canProceed) {
        e.preventDefault();
        nextStep();
        return;
      }

      // Arrow keys only work when not focused on form elements
      if (!isInputFocused) {
        if (e.key === 'ArrowRight' && canProceed) {
          e.preventDefault();
          nextStep();
        } else if (e.key === 'ArrowLeft' && step > 1) {
          e.preventDefault();
          prevStep();
        }
      }
    },
    [canProceed, isLoading, nextStep, prevStep, step],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!hasSeenTour) {
    return <WelcomeTour onComplete={completeTour} />;
  }

  return (
    <div className="min-h-screen bg-[#F4F2F2] flex flex-col">
      {/* Header */}
      <header className="w-full px-8 py-6 flex items-center justify-between max-w-4xl mx-auto">
        <div className="text-xl font-serif text-[#2E2A23]">Newsletter</div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#888185]">
            {step}/{totalSteps}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-8 h-1 rounded-full ${step >= i + 1 ? 'bg-[#E94E1B]' : 'bg-[#CAC7C7]'}`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-8 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Nome */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center space-y-2">
                <h1 className="font-serif text-3xl md:text-4xl text-[#2E2A23]">
                  Vamos nos conhecer
                </h1>
                <p className="text-[#888185] text-lg">Como podemos te chamar?</p>
              </div>

              <div className="max-w-md mx-auto">
                <Input
                  label="Seu nome"
                  placeholder="Digite seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
            </motion.div>
          )}

          {/* Step 2: Perfis */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center space-y-2">
                <h1 className="font-serif text-3xl md:text-4xl text-[#2E2A23]">
                  {t('onboarding.step1Title')}
                </h1>
                <p className="text-[#888185] text-lg">{t('onboarding.step1Subtitle')}</p>
              </div>

              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto">
                {PROFILES.map((profile, index) => (
                  <div
                    key={profile}
                    className={
                      index === PROFILES.length - 1
                        ? 'md:col-span-2 md:max-w-[calc(50%-8px)] md:mx-auto'
                        : ''
                    }
                  >
                    <ProfileCard
                      profile={profile}
                      label={t(`onboarding.profiles.${profile}`)}
                      description={t(`onboarding.profiles.${profile}_desc`)}
                      selected={selectedProfiles.includes(profile)}
                      onSelect={() => toggleProfile(profile)}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Cargo e Indústria */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center space-y-2">
                <h1 className="font-serif text-3xl md:text-4xl text-[#2E2A23]">
                  {t('onboarding.step2Title')}
                </h1>
                <p className="text-[#888185] text-lg">{t('onboarding.step2Subtitle')}</p>
              </div>

              <div className="max-w-md mx-auto space-y-6">
                <Select
                  label={t('onboarding.role')}
                  placeholder={t('onboarding.rolePlaceholder')}
                  options={ROLES}
                  value={role}
                  onChange={(e) => {
                    const selectedRole = ROLES.find((r) => r.value === e.target.value);
                    setRole(e.target.value, selectedRole?.label || '');
                  }}
                />

                <Select
                  label={t('onboarding.industry')}
                  placeholder={t('onboarding.industryPlaceholder')}
                  options={INDUSTRIES}
                  value={industry}
                  onChange={(e) => {
                    const selectedIndustry = INDUSTRIES.find((i) => i.value === e.target.value);
                    setIndustry(e.target.value, selectedIndustry?.label || '');
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="w-full px-8 py-6 border-t border-[#CAC7C7]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={prevStep}>
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {step === 3 && (
              <Button variant="ghost" onClick={skipStep3} disabled={isLoading}>
                {t('common.skip')}
              </Button>
            )}
            <Button onClick={nextStep} disabled={!canProceed || isLoading} isLoading={isLoading}>
              {step === 3 ? t('onboarding.letsGo') : t('common.continue')}
              {step < 3 && <ArrowRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
